// eslint-disable-next-line import/no-unresolved
import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';

import { EducationAssignmentResponse } from '../../mnspro-cloud/api/v2';
import { createGraphApi, GraphApiEndpoint } from '../factory';
import { ValueOf } from '../helper/helper';

class AssignmentsService {
  /**
   *
   * Gets All Submissions for One Assignment
   * @export
   * @param {string} authToken
   * @param {string} classId
   * @param {string} assignmentId
   * @return {*}  {Promise<MicrosoftGraph.EducationSubmission[]>}
   */
  static async getAllSubmissionForAssignment(
    authToken: string,
    classId: string,
    assignmentId: string,
  ): Promise<MicrosoftGraph.EducationSubmission[]> {
    const response = await createGraphApi(authToken, GraphApiEndpoint.Beta).get<
      ValueOf<MicrosoftGraph.EducationSubmission[]>
    >(
      `/education/classes/${classId}/assignments/${assignmentId}/submissions?$expand=outcomes,submittedResources,resources`,
    );

    return response.data.value;
  }

  /**
   *
   * Get Resource DriveItem
   * @export
   * @param {string} authToken
   * @param {string} FileUrl
   * @return {*}  {Promise<MicrosoftGraph.DriveItem>}
   */
  static async getResourceDriveItem(
    authToken: string,
    fileUrl: string,
  ): Promise<MicrosoftGraph.DriveItem> {
    const response = await createGraphApi(
      authToken,
      GraphApiEndpoint.V1,
    ).get<MicrosoftGraph.DriveItem>(
      fileUrl.split('https://graph.microsoft.com/v1.0')[1],
    );
    return response.data;
  }

  /**
   *
   * Get one assignment with submissions and ressources
   * @export
   * @param {string} authToken
   * @param {string} classId
   * @param {string} assignmentId
   * @return {*}  {Promise<MicrosoftGraph.EducationSubmission[]>}
   */
  static async getSpecificAssignment(
    authToken: string,
    classId: string,
    assignmentId: string,
  ): Promise<EducationAssignmentResponse> {
    const response = await createGraphApi(
      authToken,
      GraphApiEndpoint.Beta,
    ).get<EducationAssignmentResponse>(
      `/education/classes/${classId}/assignments/${assignmentId}?$expand=resources,submissions`,
    );

    return response.data;
  }

  /**
   *Gets one Submission For Assignment
   *
   * @export
   * @param {string} authToken
   * @param {string} classId
   * @param {string} assignmentId
   * @param {string} submissionId
   * @return {*}  {Promise<MicrosoftGraph.EducationSubmission>}
   */
  static async getSubmission(
    authToken: string,
    classId: string,
    assignmentId: string,
    submissionId: string,
  ): Promise<MicrosoftGraph.EducationSubmission> {
    const response = await createGraphApi(
      authToken,
      GraphApiEndpoint.Beta,
    ).get<MicrosoftGraph.EducationSubmission>(
      `/education/classes/${classId}/assignments/${assignmentId}/submissions/${submissionId}?$expand=outcomes,submittedResources,resources`,
    );

    return response.data;
  }

  /**
   *Get One Specific Assignmment
   *
   * @export
   * @param {string} authToken
   * @param {string} classId
   * @param {string} assignmentId
   * @return {*}  {Promise<MicrosoftGraph.EducationAssignment>}
   */
  static async getAssignment(
    authToken: string,
    classId: string,
    assignmentId: string,
  ): Promise<MicrosoftGraph.EducationAssignment> {
    const response = await createGraphApi(
      authToken,
      GraphApiEndpoint.Beta,
    ).get<MicrosoftGraph.EducationAssignment>(
      `education/classes/${classId}/assignments/${assignmentId}?$expand=resources,submissions`,
    );

    return response.data;
  }

  /**
   *Return Submission
   *
   * @export
   * @param {string} authToken
   * @param {string} classId
   * @param {string} assignmentId
   * @param {string} submissionId
   * @return {*}  {Promise<MicrosoftGraph.EducationAssignment>}
   */
  static async returnSubmission(
    authToken: string,
    classId: string,
    assignmentId: string,
    submissionId: string,
  ): Promise<MicrosoftGraph.EducationSubmission> {
    const response = await createGraphApi(
      authToken,
      GraphApiEndpoint.Beta,
    ).post<MicrosoftGraph.EducationSubmission>(
      `education/classes/${classId}/assignments/${assignmentId}/submissions/${submissionId}/return`,
    );

    return response.data;
  }

  /**
   *Return Submission
   *
   * @export
   * @param {string} authToken
   * @param {string} classId
   * @param {string} assignmentId
   * @param {string} submissionId
   * @return {*}  {Promise<MicrosoftGraph.EducationAssignment>}
   */
  static async unSubmitSubmission(
    authToken: string,
    classId: string,
    assignmentId: string,
    submissionId: string,
  ): Promise<MicrosoftGraph.EducationSubmission> {
    const response = await createGraphApi(
      authToken,
      GraphApiEndpoint.Beta,
    ).post<MicrosoftGraph.EducationSubmission>(
      `education/classes/${classId}/assignments/${assignmentId}/submissions/${submissionId}/unsubmit`,
    );

    return response.data;
  }

  /**
   *Return Submission
   *
   * @export
   * @param {string} authToken
   * @param {string} classId
   * @param {string} assignmentId
   * @param {string} submissionId
   * @return {*}  {Promise<MicrosoftGraph.EducationAssignment>}
   */
  static async reassignSubmission(
    authToken: string,
    classId: string,
    assignmentId: string,
    submissionId: string,
  ): Promise<MicrosoftGraph.EducationSubmission> {
    const response = await createGraphApi(
      authToken,
      GraphApiEndpoint.Beta,
    ).post<MicrosoftGraph.EducationSubmission>(
      `education/classes/${classId}/assignments/${assignmentId}/submissions/${submissionId}/reassign`,
    );

    return response.data;
  }

  /**
   *Update Education Outcome
   *
   * @export
   * @param {string} authToken
   * @param {string} classId
   * @param {string} assignmentId
   * @param {string} submissionId
   * * @param {string} outcomeId
   * @return {*}  {Promise<MicrosoftGraph.EducationAssignment>}
   */
  static async updateEducationOutcome(
    authToken: string,
    classId: string,
    assignmentId: string,
    submissionId: string,
    outcomeId: string,
    outcome: MicrosoftGraph.EducationOutcome,
  ): Promise<{
    outcome: MicrosoftGraph.EducationOutcome;
    submissionId: string;
    assignmentId: string;
  }> {
    const response = await createGraphApi(
      authToken,
      GraphApiEndpoint.Beta,
    ).patch<MicrosoftGraph.EducationOutcome>(
      `education/classes/${classId}/assignments/${assignmentId}/submissions/${submissionId}/outcomes/${outcomeId}`,
      outcome,
    );

    return { outcome: response.data, submissionId, assignmentId };
  }
}

export { AssignmentsService };
